import { useRef } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { Col,
  FloatingLabel,
  Form,
  Row,
  Button as BSButton } from "react-bootstrap";
import { usePageActions } from "..";
import { PermissionDefinitions } from "../../security";
import { LocationModel } from "../../data/entities";
import { LocationService } from "../../services";
import { Button,
  HandleDropDownListChange,
  IconAndText,
  IconDefinitions,
  SearchableOrganizationDropdown,
  Card,
  SpinnerDefault } from "../../components";
import { useAsync,
  useCRUDHandlers,
  usePermission,
  useSetPageTitle,
  useUrlParams } from "../../hooks";

export function LocationDetailsPage() {

  const id = parseInt(useUrlParams("id"));
  const locationService = new LocationService();
  const { editItem } = useCRUDHandlers<LocationModel>(locationService, "Location");
  const submitRef = useRef<HTMLButtonElement>(null);
  const { value: location, error, loading } = useAsync(() => new LocationService().get(id), [id]);

  const canEditLocation = usePermission(PermissionDefinitions.location.edit());

  const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    description: yup.string().notRequired(),
    organizationId: yup.number().required("Organization is required"),
  });

  useSetPageTitle(location?.name ?? "", [location]);
  usePageActions(
    canEditLocation && (
      <Button onClick={() => submitRef.current?.click()}>
        <IconAndText iconName={IconDefinitions.save} text="Save" />
      </Button>
    ),
    [canEditLocation]);

  if (error) return <div>Error loading location: {error.message}</div>;
  if (!location || loading) return <SpinnerDefault />;

  return (
    <Card title="Details">
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          name: location.name,
          businessCode: location.businessCode,
          locationCode: location.locationCode,
          organizationId: location.organizationId
        }}
        onSubmit={(values) => {
          const result = {
            ...location,
            name: values.name,
            businessCode: values.businessCode,
            locationCode: values.locationCode,
            organizationId: values.organizationId,
          } as LocationModel;
          editItem(result);
        }}
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
          <Form onSubmit={handleSubmit} noValidate>

            <Row>
              <Col>
                <FloatingLabel label="Name" className="mb-3">
                  <Form.Control
                    type="string"
                    placeholder="Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    disabled={!canEditLocation}
                    isInvalid={touched.name &&
                      !!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
            </Row>

            <Row>
              <Col>
                <FloatingLabel label="Business Code" className="mb-3">
                  <Form.Control
                    type="string"
                    placeholder="Business Code"
                    name="businessCode"
                    value={values.businessCode}
                    onChange={handleChange}
                    disabled={!canEditLocation}
                    isInvalid={touched.businessCode && !!errors.businessCode}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.businessCode}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
            </Row>
            <Row>
              <Col>
              <FloatingLabel label="Location Code" className="mb-3">
                <Form.Control
                  type="string"
                  name="locationCode"
                  value={values.locationCode}
                  onChange={handleChange}
                  disabled // lock the field so it can't be edited
                  isInvalid={touched.locationCode && !!errors.locationCode}
                />
                <Form.Control.Feedback type="invalid">
                {errors.locationCode}
                </Form.Control.Feedback>
              </FloatingLabel>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group controlId="organizationId">
                  <SearchableOrganizationDropdown
                    name="organizationId"
                    value={values.organizationId}
                    onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                    disabled={!canEditLocation}
                    isInvalid={touched.organizationId && !!errors.organizationId}
                  >
                    <Form.Control.Feedback type="invalid">
                      {errors.organizationId}
                    </Form.Control.Feedback>
                  </SearchableOrganizationDropdown>
                </Form.Group>
              </Col>
            </Row>

            <BSButton ref={submitRef} type="submit" className="d-none" />
          </Form>
        )}
      </Formik>
    </Card>
  );
}

export default LocationDetailsPage;

